import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  DynamicColumn,
  DynamicFieldValue,
  DynamicRow,
} from './dynamic-framework.models';

type RecordValue = string | number | boolean | null;

export interface SmartWorkflowRecordDialogData {
  title: string;
  fields: DynamicColumn[];
  row?: DynamicRow;
}

@Component({
  selector: 'app-smart-workflow-record-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
  ],
  templateUrl: './smart-workflow-record-dialog.component.html',
  styleUrls: ['./smart-workflow-record-dialog.component.css'],
})
export class SmartWorkflowRecordDialogComponent {
  readonly fields: DynamicColumn[];
  readonly values: Record<string, RecordValue> = {};
  attempted = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: SmartWorkflowRecordDialogData,
    private readonly dialogRef: MatDialogRef<
      SmartWorkflowRecordDialogComponent,
      DynamicFieldValue[]
    >,
  ) {
    this.fields = data.fields.filter(field => field.isActive);
    const existing = new Map(
      (data.row?.values ?? []).map(value => [value.columnKey, value.value]),
    );
    for (const field of this.fields) {
      this.values[field.key] = existing.get(field.key)
        ?? (field.dataType === 'boolean' ? false : null);
    }
  }

  missing(field: DynamicColumn): boolean {
    if (!this.attempted || !field.isRequired) return false;
    const value = this.values[field.key];
    return value == null || (typeof value === 'string' && !value.trim());
  }

  save(): void {
    this.attempted = true;
    if (this.fields.some(field => this.missing(field))) return;

    const values = this.fields.flatMap(field => {
      const value = this.values[field.key];
      if (value == null || (typeof value === 'string' && !value.trim())) return [];
      return [{
        fieldType: field.dataType,
        columnKey: field.key,
        value: typeof value === 'string' ? value.trim() : value,
      } satisfies DynamicFieldValue];
    });
    this.dialogRef.close(values);
  }
}
